import { useMemo, useState } from "react";
import { Button } from "../../../components/ui/button";
import { Pencil, Trash2, LayoutDashboard, Power, RotateCcw, TrendingDown, BookOpen, ChevronDown, ChevronUp, ChevronRight, CheckCircle2, AlertTriangle, XCircle, Info } from "lucide-react";
import { yearsDecimalToYM } from "./diUtils";
import { buildInstructorView, fmtDMY, fmtHM } from "../../../lib/instructorView";
import ValidityTimeline from "./ValidityTimeline";
import CardDocuments from "../../../components/CardDocuments";
import InstructionRecordsDialog from "./InstructionRecordsDialog";

const LICENSE_STATUS_LABEL = {
  S: { text: "S", title: "Instrutor de Instrutores", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
  R: { text: "R", title: "Restrito",                 cls: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
};

const TONE = {
  ok:   { dot: "bg-emerald-400", tile: "border-slate-700 bg-slate-900/50",             icon: "text-emerald-400", text: "text-emerald-300", bar: "bg-emerald-500", Icon: CheckCircle2 },
  info: { dot: "bg-sky-400",   tile: "border-sky-500/40 bg-sky-500/10",            icon: "text-sky-300",   text: "text-sky-300",   bar: "bg-sky-400",   Icon: Info },
  warn: { dot: "bg-amber-400",   tile: "border-amber-500/50 bg-amber-500/10",          icon: "text-amber-400",   text: "text-amber-300",   bar: "bg-amber-400",   Icon: AlertTriangle },
  bad:  { dot: "bg-red-500",     tile: "border-red-500/60 bg-red-500/10",              icon: "text-red-400",     text: "text-red-300",     bar: "bg-red-500",     Icon: XCircle },
};

const KIND_CHIP = {
  ok: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  warn: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  grounded: "bg-red-500/15 text-red-300 border-red-500/40",
  inactive: "bg-slate-700/60 text-slate-300 border-slate-600/50",
};

const GO_TITLE = { edit: "Abrir a ficha para atualizar", log: "Abrir o diário de voo", records: "Ver os registos de instrução da caderneta" };

function daysText(r) {
  if (r.days == null) return "";
  return r.days < 0 ? `expirou há ${-r.days} ${-r.days === 1 ? "dia" : "dias"}` : `${r.days} ${r.days === 1 ? "dia" : "dias"}`;
}

function Block({ title, children, testid }) {
  return (
    <div className="mt-3 pt-3 border-t border-slate-700/60" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">{title}</div>
      {children}
    </div>
  );
}

// Progresso para perder a restrição (R → S), só de leitura: o cartão mostra que pode dar instrução com restrições
function RestrictionProgress({ r }) {
  const pct = (a, b) => Math.min(100, Math.round((100 * a) / (b || 1)));
  const hPct = pct(r.instrMin, r.requiredInstrMin);
  const sPct = pct(r.solos, r.requiredSolos);
  return (
    <div className="mt-3 p-3 rounded border border-amber-500/40 bg-amber-500/5 space-y-2.5" data-testid="di-restriction-progress">
      <div className="text-[12px] text-amber-200 font-semibold">
        Restrito (R): pode dar instrução com restrições. Para perder a restrição:
      </div>
      <div>
        <div className="flex justify-between text-[11px] text-slate-300">
          <span>Horas de instrução</span>
          <span className={`font-mono ${r.instrMin >= r.requiredInstrMin ? "text-emerald-300" : "text-amber-300"}`}>{fmtHM(r.instrMin)} de {fmtHM(r.requiredInstrMin)}</span>
        </div>
        <div className="mt-1 h-2 rounded-full bg-slate-800 overflow-hidden">
          <div className={`h-full rounded-full ${hPct >= 100 ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${hPct}%` }} />
        </div>
      </div>
      <div>
        <div className="flex justify-between text-[11px] text-slate-300">
          <span>Voos solo (supervisionados e largada)</span>
          <span className={`font-mono ${r.solos >= r.requiredSolos ? "text-emerald-300" : "text-amber-300"}`}>{r.solos} de {r.requiredSolos}</span>
        </div>
        <div className="mt-1 h-2 rounded-full bg-slate-800 overflow-hidden">
          <div className={`h-full rounded-full ${sPct >= 100 ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${sPct}%` }} />
        </div>
      </div>
      <div className="text-[11px] text-slate-300">
        Relatório do supervisor:{" "}
        {r.reportOk
          ? <span className="text-emerald-300">aprovado{r.reportApprovedAt ? ` em ${fmtDMY(r.reportApprovedAt)}` : ""}</span>
          : <span className="text-amber-300">por aprovar</span>}
      </div>
      {r.allOk && <div className="text-[11px] text-emerald-300 font-semibold">Todos os critérios cumpridos: a restrição sai.</div>}
    </div>
  );
}

export default function InstructorCard({ instructor: i, expanded, onToggle, onOpenLog, onOpenDashboard, onEdit, onDelete, onToggleActive, onDemoted, docCount = 0 }) {
  const v = useMemo(() => buildInstructorView(i), [i]);
  const [recordsOpen, setRecordsOpen] = useState(false);
  const [hoursOpen, setHoursOpen] = useState(false);
  const licRev = v.revalidations.find((r) => r.key === "instructor_license");
  const lsBadge = i.license_status ? LICENSE_STATUS_LABEL[i.license_status] : null;
  const isInactive = i.active === false;
  const hasSourcePilot = !!i.source_pilot_id;
  const cardClass = isInactive
    ? "rounded-lg border border-slate-700 bg-slate-900/40 p-4 opacity-60"
    : v.kind === "grounded"
      ? "rounded-lg border-2 border-red-500/60 bg-red-500/[0.04] p-4 ring-1 ring-red-500/20"
      : v.kind === "warn"
        ? "rounded-lg border border-amber-500/40 bg-amber-500/[0.03] p-4"
        : "rounded-lg border border-cockpit-border bg-slate-900/40 p-4";
  const go = (target) => (target === "log" ? onOpenLog?.() : target === "records" ? setRecordsOpen(true) : onEdit?.());
  const hoursTone = v.conditions.find((c) => c.key === "hours")?.tone || "ok";

  return (
    <div className={cardClass} data-testid={`di-card-${i.pu_number}`}>
      <InstructionRecordsDialog
        open={recordsOpen}
        onClose={() => setRecordsOpen(false)}
        title={`Registos de instrução da caderneta · ${i.name}`}
        subtitle={licRev?.instr?.windowStart ? `Instrução nos 12 meses antes de ${fmtDMY(licRev.validUntil)}: a janela vai de ${fmtDMY(licRev.instr.windowStart)} a ${fmtDMY(licRev.instr.windowEnd)}.` : null}
        records={licRev?.instr?.records}
        totalMin={licRev?.instr?.totalMin}
        requiredMin={licRev?.instr?.requiredMin}
        footnote="Só contam os minutos de instrução dados como instrutor no próprio voo. Os voos em que o instrutor foi acompanhante aparecem, mas não contam nesta conta."
      />
      <InstructionRecordsDialog
        open={hoursOpen}
        onClose={() => setHoursOpen(false)}
        title={`Voos que contam nas horas dos últimos 12 meses · ${i.name}`}
        subtitle={v.hours12.window ? `Voos de ${fmtDMY(v.hours12.window.start)} a ${fmtDMY(v.hours12.window.end)}: tempo de voo como piloto e como acompanhante (duplo comando).` : null}
        records={v.hours12.records}
        totalMin={v.hours12.have}
        requiredMin={v.hours12.need}
        countLabel="Total"
        showNotCounted={false}
        footnote="É a soma destes voos que aparece no cartão. O mínimo são 15:00 horas nos últimos 12 meses."
      />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <button type="button" onClick={onToggle} aria-expanded={expanded} className="flex-1 min-w-[15rem] text-left" data-testid={`di-toggle-${i.pu_number}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-base text-slate-100 truncate">{i.name}</span>
            <span className="font-mono text-xs text-slate-400">PU {i.pu_number}</span>
            {lsBadge && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${lsBadge.cls}`} title={lsBadge.title}>{lsBadge.text}</span>}
            {isInactive && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 tracking-wide" data-testid={`di-instructor-inactive-${i.pu_number}`}>INACTIVO</span>}
          </div>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <span className={`inline-block text-[11px] px-2 py-0.5 rounded border font-semibold ${KIND_CHIP[v.kind]}`} data-testid="di-instructor-state">{v.phrase}</span>
            <span className="text-[12px] text-slate-300 min-w-0" data-testid="di-instructor-reason">{v.reason}</span>
          </div>
        </button>
        <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
          <CardDocuments ownerKind="instructor" ownerId={i.id} count={docCount} />
          <Button size="icon" variant="ghost" onClick={onOpenDashboard} title="Dashboard" data-testid={`di-open-dashboard-${i.pu_number}`} className="text-slate-400 hover:text-orange-300"><LayoutDashboard className="w-3.5 h-3.5" /></Button>
          <Button size="icon" variant="ghost" onClick={onOpenLog} title="Diário de voo" data-testid={`di-open-log-${i.pu_number}`} className="text-slate-400 hover:text-blue-300"><BookOpen className="w-3.5 h-3.5" /></Button>
          <Button size="icon" variant="ghost" onClick={onEdit} title="Editar" data-testid={`di-edit-${i.pu_number}`} className="text-slate-400 hover:text-orange-300"><Pencil className="w-3.5 h-3.5" /></Button>
          {!isInactive && (
            <Button
              size="icon"
              variant="ghost"
              title={hasSourcePilot
                ? "Despromover a Piloto — arquiva instrutor e reactiva o piloto original"
                : "Despromover a Piloto — arquiva instrutor e cria nova ficha de piloto"}
              onClick={() => {
                const msg = hasSourcePilot
                  ? `Despromover ${i.name} a Piloto?\n\nA ficha de instrutor será arquivada (inactiva). O piloto original será reactivado com todo o histórico anterior.\n\nOs voos do período como instrutor ficam preservados.`
                  : `Despromover ${i.name} a Piloto?\n\nSem ficha de piloto anterior, será criada uma nova ficha de Piloto Regular com os dados actuais do instrutor.\n\nA ficha de instrutor será arquivada (inactiva). Os voos ficam preservados.`;
                if (!window.confirm(msg)) return;
                onDemoted?.(i.id);
              }}
              data-testid={`di-demote-pilot-${i.pu_number}`}
              className="text-slate-400 hover:text-amber-300"
            >
              <TrendingDown className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button size="icon" variant="ghost" onClick={onToggleActive} className={isInactive ? "text-emerald-400" : "text-amber-300"} data-testid={`di-toggle-active-${i.pu_number}`} title={isInactive ? "Reactivar instrutor" : "Desactivar instrutor"}>
            {isInactive ? <RotateCcw className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
          </Button>
          <Button size="icon" variant="ghost" onClick={onDelete} className="text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
          <button type="button" onClick={onToggle} className="p-1 text-slate-500" aria-label={expanded ? "Fechar detalhes" : "Abrir detalhes"}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* As cinco condições para dar instrução, sempre à vista: cinco mosaicos iguais (nome, valor, o que falta) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3" data-testid="di-instructor-conditions">
        {v.conditions.map((c) => {
          const t = TONE[c.tone];
          return (
            <div key={c.key} className={`rounded-lg border px-2.5 py-2 min-w-0 ${t.tile}`} data-testid={`di-condition-${c.key}`} title={`${c.label}: ${c.detail}`}>
              <div className="flex items-center justify-between gap-1">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 truncate">{c.short}</span>
                <t.Icon className={`w-3.5 h-3.5 shrink-0 ${t.icon}`} aria-hidden="true" />
              </div>
              <div className="font-mono text-[13px] leading-tight text-slate-100 mt-0.5 break-words">{c.value}</div>
              <div className={`text-[11px] leading-snug ${c.tone === "ok" ? "text-slate-400" : t.text}`}>{c.sub}</div>
              {c.key === "medical" && v.info.medicalNumber && (
                <div className="text-[10px] text-slate-500 mt-0.5" data-testid="di-condition-medical-number">Nº médico {v.info.medicalNumber}</div>
              )}
              {c.key === "instructor_license" && (
                <div className="text-[10px] text-slate-500 mt-0.5" data-testid="di-condition-license-issued">Emitida em {fmtDMY(v.info.licenseIssuedAt)} · {yearsDecimalToYM(v.info.years)} de experiência</div>
              )}
            </div>
          );
        })}
      </div>

      {expanded && (
        <div data-testid="di-instructor-details">
          <Block title="Dados do instrutor" testid="di-instructor-data">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="px-3 py-2 rounded border border-slate-700 bg-slate-900/50">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Nº do certificado médico</div>
                <div className="font-mono text-sm text-slate-100">{v.info.medicalNumber || "—"}</div>
              </div>
              <div className="px-3 py-2 rounded border border-slate-700 bg-slate-900/50">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Licença de instrutor emitida em</div>
                <div className="font-mono text-sm text-slate-100">{fmtDMY(v.info.licenseIssuedAt)}</div>
              </div>
              <div className="px-3 py-2 rounded border border-slate-700 bg-slate-900/50">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Experiência como instrutor</div>
                <div className="font-mono text-sm text-slate-100">{yearsDecimalToYM(v.info.years)}</div>
              </div>
            </div>
          </Block>

          <Block title="Pode dar instrução?" testid="di-instructor-can">
            <button type="button" onClick={() => setHoursOpen(true)} className="text-left text-[12px] text-slate-300 hover:text-white" data-testid="di-instructor-hours12" title="Ver os voos que contam nestas horas">
              Horas nos últimos 12 meses: <span className={`font-mono ${TONE[hoursTone].text}`}>{fmtHM(v.hours12.have)}</span> · mínimo <span className="font-mono">{fmtHM(v.hours12.need)}</span>
              <span className="ml-2 text-[10px] text-sky-300">ver os voos</span>
            </button>
            <div className="mt-1.5 h-2 rounded-full bg-slate-800 overflow-hidden max-w-md">
              <div className={`h-full rounded-full ${TONE[hoursTone].bar}`} style={{ width: `${v.hours12.pct}%` }} />
            </div>
          {v.restricted && <RestrictionProgress r={v.restricted} />}
          </Block>

          <Block title="Horas" testid="di-instructor-hours">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {v.metrics.map((m) => (
                <div key={m.label} className={`px-3 py-2 rounded border ${m.tone ? TONE[m.tone].tile : "border-slate-700 bg-slate-900/50"}`}>
                  <div className={`font-mono text-base ${m.tone ? TONE[m.tone].text : "text-slate-100"}`}>{m.value}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{m.label}</div>
                </div>
              ))}
            </div>
            {v.initial && (
              <div className="mt-2 text-[12px] px-3 py-1.5 rounded border border-sky-500/30 bg-sky-500/10 text-sky-200" data-testid="di-instructor-initial-hours">
                Horas iniciais (antes da aplicação): <span className="font-mono">{v.initial.pic}</span> de piloto · <span className="font-mono">{v.initial.instr}</span> de instrução
              </div>
            )}
          </Block>

          <Block title="Revalidações" testid="di-instructor-revalidations">
            {i.proof_check_date && (
              <p className="text-[11px] text-slate-300 mb-2" data-testid="di-instructor-proof-check">Prova com examinador feita em {fmtDMY(i.proof_check_date)} · aguarda a emissão da ANAC; a validade é introduzida quando chegar o documento.</p>
            )}
            <div className="rounded border border-slate-700/60 overflow-hidden">
              {v.revalidations.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => go(r.target)}
                  title={GO_TITLE[r.target]}
                  className="w-full text-left flex items-start gap-2.5 px-3 py-2 border-t border-slate-700/60 first:border-t-0 hover:bg-slate-800/60 focus:outline-none focus-visible:bg-slate-800/60 transition-colors"
                  data-testid={`di-reval-${r.key}`}
                >
                  <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${TONE[r.tone].dot}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-medium text-slate-100">{r.title}</div>
                    <div className="text-[11px] text-slate-400 leading-snug">{r.rule}</div>
                    {r.progress && <div className={`text-[11px] mt-0.5 ${r.progressTone ? TONE[r.progressTone].text : "text-slate-200"}`}>{r.progress}</div>}
                    {r.target === "records" && <div className="text-[10px] text-sky-300 mt-0.5">Ver os registos de instrução da caderneta</div>}
                    {r.pct != null && (
                      <div className="mt-1 h-1.5 rounded-full bg-slate-800 overflow-hidden max-w-[260px]">
                        <div className={`h-full rounded-full ${TONE[r.progressTone || r.tone].bar}`} style={{ width: `${r.pct}%` }} />
                      </div>
                    )}
                  </div>
                  <span className={`text-[11px] whitespace-nowrap pt-0.5 ${TONE[r.tone].text}`}>{daysText(r)}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-500 shrink-0 mt-1" aria-hidden="true" />
                </button>
              ))}
            </div>
          </Block>

          <Block title="Validades nos próximos 12 meses" testid="di-instructor-timeline">
            <ValidityTimeline entries={v.timeline} />
          </Block>
        </div>
      )}
    </div>
  );
}
